/**
 * Vincent #280 (2026-06-11) — Panel "Accès utilisateurs" de la Messagerie.
 *
 * Matrice CANAUX (lignes) × RÔLES / clés techniques (colonnes), représentés
 * exactement comme dans "Sélection des accès" (8 rôles canoniques avec les
 * libellés personnalisés de l'org).
 *
 * Sémantique :
 *   - allowed_role_keys = null  → canal ouvert à tous (toutes cases cochées)
 *   - allowed_role_keys = []    → canal fermé (admins seulement)
 *   - allowed_role_keys = [...] → restreint à ces rôles
 *
 * Endpoints :
 *   GET /api/channels/role-access-matrix
 *   PUT /api/channels/role-access-matrix/bulk
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FloppyDisk, SpinnerGap, ShieldCheck } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { getCanonicalLabel, roleLabelsFor } from "../../lib/canonicalRoles";
import PollAccessPanel from "./PollAccessPanel";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChannelAccessMatrixPanel() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  // state[channelId] = Set(roleKeys) — toujours matérialisé en liste explicite
  const [matrix, setMatrix] = useState({});
  const [initialMatrix, setInitialMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/channels/role-access-matrix`, { withCredentials: true });
      const chs = data.channels || [];
      const rls = data.roles || [];
      setChannels(chs);
      setRoles(rls);
      const m = {};
      chs.forEach((c) => {
        // null = ouvert à tous → toutes cases cochées.
        // Admin + Fondateur sont TOUJOURS inclus (ils voient tout côté backend).
        const ark = c.allowed_role_keys;
        m[c.id] = new Set([...(Array.isArray(ark) ? ark : rls), "admin", "founder"]);
      });
      setMatrix(m);
      setInitialMatrix(Object.fromEntries(Object.entries(m).map(([k, v]) => [k, new Set(v)])));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de charger la matrice d'accès");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    return Object.keys(matrix).some((cid) => {
      const a = matrix[cid] || new Set();
      const b = initialMatrix[cid] || new Set();
      if (a.size !== b.size) return true;
      for (const k of a) if (!b.has(k)) return true;
      return false;
    });
  }, [matrix, initialMatrix]);

  const toggle = (channelId, roleKey) => {
    setMatrix((prev) => {
      const next = { ...prev };
      const set = new Set(next[channelId] || []);
      if (set.has(roleKey)) set.delete(roleKey);
      else set.add(roleKey);
      next[channelId] = set;
      return next;
    });
  };

  const setAllForChannel = (channelId, checked) => {
    setMatrix((prev) => ({ ...prev, [channelId]: new Set(checked ? roles : ["admin", "founder"]) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updates = channels.map((c) => {
        const set = matrix[c.id] || new Set();
        // Si tous les rôles sont cochés → null (canal ouvert, rétrocompat)
        const allChecked = roles.every((r) => set.has(r));
        return { channel_id: c.id, allowed_role_keys: allChecked ? null : Array.from(set) };
      });
      await axios.put(`${API}/api/channels/role-access-matrix/bulk`, { updates }, { withCredentials: true });
      toast.success(lang === "fr" ? "Accès aux canaux enregistrés" : "Channel access saved");
      setInitialMatrix(Object.fromEntries(Object.entries(matrix).map(([k, v]) => [k, new Set(v)])));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-zinc-400 gap-2">
        <SpinnerGap size={18} className="animate-spin" />
        {lang === "fr" ? "Chargement de la matrice…" : "Loading matrix…"}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 sm:p-6" data-testid="channel-access-matrix-panel">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-bold text-zinc-900 flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-[#002FA7]" />
            {lang === "fr" ? "Accès aux canaux par rôle" : "Channel access by role"}
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            {lang === "fr"
              ? "Pour chaque canal (ligne), cochez les rôles (colonnes) qui peuvent y accéder. Les rôles correspondent aux clés techniques de la Sélection des accès. Les Admin et Fondateur voient toujours tous les canaux."
              : "For each channel (row), check the roles (columns) that can access it. Admin and Founder always see all channels."}
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#00248a] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="role-matrix-save-btn"
        >
          {saving ? <SpinnerGap size={15} className="animate-spin" /> : <FloppyDisk size={15} />}
          {lang === "fr" ? "Enregistrer" : "Save"}{dirty ? " *" : ""}
        </button>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-xl bg-white">
        <table className="min-w-full text-sm" data-testid="role-matrix-table">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="sticky left-0 bg-zinc-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 z-10">
                {lang === "fr" ? "Canal" : "Channel"}
              </th>
              {roles.map((r) => (
                <th key={r} className="px-3 py-3 text-center text-[11px] font-semibold text-zinc-600 whitespace-nowrap" data-testid={`role-col-${r}`}>
                  <div>{getCanonicalLabel(r, roleLabelsFor(user, lang), r === "founder", lang)}</div>
                  <div className="text-[9px] font-mono text-zinc-400 normal-case">{r}</div>
                </th>
              ))}
              <th className="px-3 py-3 text-center text-[11px] font-semibold text-zinc-500 whitespace-nowrap">
                {lang === "fr" ? "Tout" : "All"}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {channels.map((c) => {
              const set = matrix[c.id] || new Set();
              const allChecked = roles.every((r) => set.has(r));
              return (
                <tr key={c.id} className="hover:bg-zinc-50/60" data-testid={`role-matrix-row-${c.id}`}>
                  <td className="sticky left-0 bg-white px-4 py-2.5 font-medium text-zinc-800 whitespace-nowrap z-10">
                    <span className="mr-1.5">{c.emoji}</span>
                    {c.name}
                    <span className="ml-2 text-[10px] text-zinc-400">
                      {set.size}/{roles.length}
                    </span>
                  </td>
                  {roles.map((r) => {
                    const isAlwaysOn = r === "admin" || r === "founder";
                    const checked = isAlwaysOn || set.has(r);
                    return (
                      <td key={r} className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isAlwaysOn}
                          onChange={() => toggle(c.id, r)}
                          className="w-4 h-4 accent-[#002FA7] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                          data-testid={`role-matrix-cell-${c.id}-${r}`}
                          title={isAlwaysOn ? (lang === "fr" ? "Les admins/fondateurs voient toujours tous les canaux" : "Admins/founders always see all channels") : undefined}
                        />
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center whitespace-nowrap">
                    <button
                      onClick={() => setAllForChannel(c.id, !allChecked)}
                      className="text-[10px] px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-100 text-zinc-600 font-semibold"
                      data-testid={`role-matrix-toggle-all-${c.id}`}
                    >
                      {allChecked ? (lang === "fr" ? "Aucun" : "None") : (lang === "fr" ? "Tous" : "All")}
                    </button>
                  </td>
                </tr>
              );
            })}
            {channels.length === 0 && (
              <tr>
                <td colSpan={roles.length + 2} className="px-4 py-10 text-center text-zinc-400">
                  {lang === "fr" ? "Aucun canal." : "No channel."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Tâche 1424 — Le droit de créer des sondages se coche PAR PERSONNE,
          dans son propre tableau. Une colonne de plus dans la matrice
          canaux × rôles ci-dessus ferait cocher un droit global dans une
          grille qui ne parle, ligne par ligne, que d'un canal. */}
      <div className="mt-8 border-t border-zinc-200 pt-6">
        <PollAccessPanel />
      </div>
    </div>
  );
}
